// Thin wrapper around the channels service's channel + channel-policy
// endpoints (see services/channels/src/channels_service/routers/
// channels.py and routers/channel_policies.py).
//
// Same as policiesApi.js/draftsApi.js: the frontend and the reverse
// proxy always share one origin (see services/proxy), so a relative
// path is all we need here - no base URL or CORS configuration
// required.
const API_BASE = '/api';

export class ChannelsError extends Error {
  constructor(message, { code, status } = {}) {
    super(message);
    this.name = 'ChannelsError';
    this.code = code;
    this.status = status;
  }
}

/**
 * Shared fetch + error-shape handling for every call in this module -
 * same JSON-body/error_description convention as policiesApi.js's
 * `request`.
 */
async function request(accessToken, path, { method = 'GET', body } = {}) {
  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${accessToken}`,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch (err) {
    throw new ChannelsError(
      'Could not reach the server. Check your connection and try again.',
      { code: 'network_error' },
    );
  }

  let parsed = null;
  try {
    parsed = await response.json();
  } catch (err) {
    // Non-JSON response body (e.g. a 204); fall through with parsed === null.
  }

  if (!response.ok) {
    const message = parsed?.error_description ?? 'Something went wrong. Please try again.';
    throw new ChannelsError(message, { code: parsed?.error, status: response.status });
  }

  return parsed;
}

/**
 * Calls GET /api/channels. Keyset-paginated (see `Page` in
 * schemas.py) - `?owner=` and `?q=` narrow it further, same as the
 * backend does. Passing `owner` (e.g. the signed-in user's own
 * username) is what makes this "my channels" rather than "every
 * channel I can see" - the backend otherwise mixes in every other
 * public channel too.
 *
 * Resolves with `{ items: ChannelOut[], nextCursor: string|null }` on
 * success - pass `nextCursor` back in as `cursor` to fetch the next
 * page. Rejects with a ChannelsError otherwise.
 */
export async function listChannels(accessToken, { owner, q, cursor, limit = 20 } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (owner) params.set('owner', owner);
  if (q) params.set('q', q);
  if (cursor) params.set('cursor', cursor);
  const body = await request(accessToken, `/channels?${params}`);
  return {
    items: Array.isArray(body?.items) ? body.items : [],
    nextCursor: body?.next_cursor ?? null,
  };
}

/**
 * Calls POST /api/channels - creates a new channel owned by the caller
 * (see `create_channel` in routers/channels.py). `visibility` defaults
 * to 'public' server-side when omitted.
 *
 * Resolves with the created `ChannelOut` (HTTP 201) on success. Rejects
 * with a ChannelsError otherwise - notably 409 (`channel_exists`) if
 * the caller already owns a channel with that name.
 */
export async function createChannel(accessToken, { name, description, visibility }) {
  return request(accessToken, '/channels', {
    method: 'POST',
    body: {
      name,
      description: description || null,
      visibility: visibility || 'public',
    },
  });
}

/**
 * Calls GET /api/channels/{owner}/{name}.
 *
 * Resolves with the `ChannelOut` on success. Rejects with a
 * ChannelsError otherwise - notably 404 (`not_found`), which is also
 * what a private channel the caller doesn't own looks like (visibility
 * is never leaked - see `ensure_visible` in routers/channels.py).
 */
export async function getChannel(accessToken, owner, name) {
  return request(accessToken, `/channels/${owner}/${name}`);
}

/**
 * Calls GET /api/channels/{owner}/{name}/packages.
 *
 * Resolves with `{ packageCount: number, names: string[] }` on success -
 * `packageCount` is the exact distinct-name total, `names` the first few
 * (server caps the listing at 10). Rejects with a ChannelsError
 * otherwise (404 `not_found`).
 */
export async function getChannelPackages(accessToken, owner, name) {
  const body = await request(accessToken, `/channels/${owner}/${name}/packages`);
  return {
    packageCount: body?.package_count ?? 0,
    names: Array.isArray(body?.names) ? body.names : [],
  };
}

/**
 * Calls GET /api/channels/{owner}/{name}/policies.
 *
 * Resolves with the `ChannelPoliciesOut` (the ordered binding list plus
 * `policy_set_hash`/`admits_all`) on success. Rejects with a
 * ChannelsError otherwise (404 `not_found`).
 */
export async function getChannelPolicies(accessToken, owner, name) {
  return request(accessToken, `/channels/${owner}/${name}/policies`);
}

/**
 * Calls PUT /api/channels/{owner}/{name}/policies - replaces the whole
 * ordered binding list in one shot (see `put_channel_policies` in
 * routers/channel_policies.py). `bindings` is `{ policy, enabled }[]`
 * in the desired order - order matters and is user-visible (last match
 * wins across the whole list). Only the channel's owner may call this
 * (403 otherwise).
 *
 * Resolves with the updated `ChannelPoliciesOut` on success. Rejects
 * with a ChannelsError otherwise - notably 409 (`default_effect_conflict`,
 * enabled bindings disagree on their policy's default effect) or 422
 * (`unbindable_policy`/`invalid_bindings`).
 */
export async function putChannelPolicies(accessToken, owner, name, bindings) {
  return request(accessToken, `/channels/${owner}/${name}/policies`, {
    method: 'PUT',
    body: { bindings },
  });
}
